import { MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { Button } from "components/atoms/Button";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import NumberInput from "components/atoms/NumberInput/NumberInput";
import { Header } from "components/atoms/Typography";
import Sidebar from "components/molecules/Sidebar";
import MUITheme from "components/MUITheme";
import { useCustomPlanUpdate } from "hooks/CustomPlan/useCustomPlanUpdate";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import useMeasureSidebarState from "recoilStore/CustomPlan/useMeasureSidebarState";
import { projectsCustomPlan } from "utils/constants";
import GenericMeasureInputs, { DEFAULT_MEASURE_INPUTS } from "./MeasureEffectInputs";
import MeasureOverrides from "./MeasureOverrides";
import RevenueEffectInput from "./RevenueEffectInput";

interface NewProjectSidebarProps {
    closeSidebar: () => void;
    sidebarOpen: boolean;
    buildingModelUid: string;
    currentCarbonReductionPlanUid: string;
}

function NewProjectSidebar({
    closeSidebar,
    sidebarOpen,
    buildingModelUid,
    currentCarbonReductionPlanUid,
}: NewProjectSidebarProps) {
    const { state: measureSidebarState, setState: setMeasureSidebarState } = useMeasureSidebarState();

    const [measureInputs, setMeasureInputs] = useState<CarbonReductionMeasureInput>(DEFAULT_MEASURE_INPUTS);

    const [isApplyDisabled, setIsApplyDisabled] = useState(true);

    useEffect(() => {
        if (sidebarOpen && measureSidebarState.createMeasurePrefill != null) {
            setMeasureInputs(measureSidebarState.createMeasurePrefill);
            setIsApplyDisabled(false);
            setMeasureSidebarState((prev) => ({ ...prev, createMeasurePrefill: null }));
        }
    }, [sidebarOpen, measureSidebarState.createMeasurePrefill, setMeasureSidebarState]);

    const measureDropdownItems = [...projectsCustomPlan, ...[{ value: "generic", label: "Generic Measure" }]];

    const resetProjectState = () => {
        setMeasureInputs(DEFAULT_MEASURE_INPUTS);
    };

    const handleProjectChange = (e: SelectChangeEvent<string>) => {
        const crmId = e.target.value;
        resetProjectState();
        if (crmId) {
            setIsApplyDisabled(false);
        } else {
            setIsApplyDisabled(true);
        }
        setMeasureInputs((prev) => ({ ...prev, crmId }));
    };

    const onClickCancel = () => {
        resetProjectState();
        closeSidebar();
    };

    const { updateCustomPlanMeasures, isLoading } = useCustomPlanUpdate({
        buildingModelUid,
        buildingModelCarbonReductionPlanId: currentCarbonReductionPlanUid,
    });

    const onClickApply = () => {
        posthog.capture("Add measure");
        updateCustomPlanMeasures("create", {
            onSuccess: () => {
                closeSidebar();
                resetProjectState();
            },
            measure: measureInputs,
        });
    };

    if (isLoading)
        return (
            <Sidebar onCancel={closeSidebar} sidebarOpen={sidebarOpen} title="Add Measure">
                <LoadingSpinner />
            </Sidebar>
        );

    return (
        <Sidebar onCancel={onClickCancel} sidebarOpen={sidebarOpen} title="Add Measure">
            <div className="filter-container">
                <Header size="small">Select Measure</Header>
                <MUITheme primaryColor="#EB03AD">
                    <Select
                        value={measureInputs.crmId || ""}
                        onChange={handleProjectChange}
                        style={{
                            width: "100%",
                            height: "35px",
                            minWidth: 0,
                        }}
                        required
                    >
                        {measureDropdownItems
                            .sort((a, b) => a.label.localeCompare(b.label))
                            .map((project: ProjectCustomPlan) => (
                                <MenuItem key={project.value} value={project.value}>
                                    {project.label}
                                </MenuItem>
                            ))}
                    </Select>
                </MUITheme>
            </div>
            <div className="filter-container">
                <Header size="small">Implementation Year</Header>
                <NumberInput
                    key="yearBuiltOriginal"
                    required={true}
                    value={measureInputs.year || ""}
                    className="filter-input"
                    onChange={(e) => {
                        const year = Number(e.target.value);
                        setMeasureInputs((prev) => ({
                            ...prev,
                            year,
                        }));
                    }}
                    placeholder="Enter project year"
                    maxLength={50}
                />
            </div>
            <MeasureOverrides measure={null} measureInputs={measureInputs} setMeasure={setMeasureInputs} />
            {measureInputs.crmId === "generic" ? (
                <GenericMeasureInputs
                    inputs={measureInputs}
                    setInputs={setMeasureInputs}
                    exclude={["measureCost", "measureSize"]}
                />
            ) : (
                <RevenueEffectInput
                    inputs={measureInputs.revenueEffect}
                    setInputs={(revenueInputs) =>
                        setMeasureInputs({
                            ...measureInputs,
                            revenueEffect: revenueInputs,
                        })
                    }
                />
            )}
            {/* Footer */}
            <div className="filters-sidebar__footer">
                <div className="filters-sidebar__footer__buttons">
                    <Button type="primary" onClick={onClickApply} disabled={isLoading || isApplyDisabled}>
                        {isLoading ? "Loading" : "Apply"}
                    </Button>

                    {!isLoading && (
                        <Button onClick={onClickCancel} type="link" disabled={isLoading}>
                            Cancel
                        </Button>
                    )}
                </div>
            </div>
        </Sidebar>
    );
}

export default NewProjectSidebar;
